import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getTimezone } from '@/components/contexts/TimezoneContext';
import { BreadcrumbNavigation } from '@/components/folders';
import { FolderTree } from '@/components/folders/FolderTree';
import { FileUpload, Loading } from '@/components/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SelectionStateContext } from '@/components/ui/column-builders';
import { ColumnsCaseFilesSelectionMode } from '@/components/ui/columns-case-files';
import { ColumnsCorrespondenceSelectionMode } from '@/components/ui/columns-correspondence';
import { DataTable } from '@/components/ui/data-table';
import {
  SUPPORTED_FILE_TYPES_STRING
} from '@/constants/document-constants';
import { useBreadcrumbPath } from '@/hooks/useBreadcrumbPath';
import { useDeepAnalysisAvailability } from '@/hooks/useDeepAnalysisAvailability';
import { type Document, useDocuments } from '@/hooks/useDocuments';
import { type FolderTree as FolderTreeType, useFolderTree } from '@/hooks/useFolders';
import { cn } from '@/lib/utils';
import type { DocumentSelection } from '@/types';
import { isCorrespondenceFolder as checkIsCorrespondenceFolder } from '@/utils/document-utils';
import type { LucideIcon } from 'lucide-react';
import { SelectedDocumentsPanel } from './SelectedDocumentsPanel';

export interface DocumentStatusIcon {
  Icon: LucideIcon;
  className: string;
  tooltip: string;
}

export interface SelectionDisabledResult {
  disabled: boolean;
  reason?: string;
}

export interface DocumentFilterToggle {
  id: string;
  label: string;
  defaultValue: boolean;
  /** Return true to INCLUDE the document when this toggle is ON.
   *  When the toggle is OFF, all documents are shown (no filtering). */
  predicate: (doc: Document) => boolean;
}

interface DocumentSelectorProps {
  matterSlug: string;
  documentSelections: Map<string, DocumentSelection>;
  onToggleSelection: (doc: Document) => void;
  onClearSelection: () => void;
  showUploadButton?: boolean;
  onUploadComplete?: (fileNames?: string[]) => void;
  onDone?: () => void;
  doneLabel?: string;
  doneDisabled?: boolean;
  className?: string;
  getDocumentStatusIcons?: (doc: Document) => DocumentStatusIcon[];
  getDocumentIconOverlay?: (doc: Document) => React.ReactNode;
  isSelectionDisabled?: (doc: Document) => SelectionDisabledResult;
  filterToggles?: DocumentFilterToggle[];
  /** Client-side filter applied after the folder-scoped fetch. */
  documentPredicate?: (doc: Document) => boolean;
  shouldHideFolder?: (folder: FolderTreeType) => boolean;
  selectedFolderGuidsForChecked?: Set<string>;
  onFolderToggleSelection?: (folder: FolderTreeType) => void;
  /** Doc guids rendered as checked even when not in `documentSelections`. */
  implicitlySelectedDocGuids?: Set<string>;
  /** Whether Deep Analysis badges should be shown on included folders. Defaults to matter feature availability. */
  showDeepAnalysis?: boolean;
  topBanner?: React.ReactNode;
  footerExtraContent?: React.ReactNode;
}

/**
 * Reusable document selector component with folder tree navigation and file table.
 * Mirrors the Case Files interface but designed for document selection workflows.
 */
export const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  matterSlug,
  documentSelections,
  onToggleSelection,
  onClearSelection,
  showUploadButton = true,
  onUploadComplete,
  onDone,
  doneLabel = 'Done',
  doneDisabled = false,
  className,
  getDocumentStatusIcons,
  getDocumentIconOverlay,
  isSelectionDisabled,
  filterToggles,
  documentPredicate,
  shouldHideFolder,
  selectedFolderGuidsForChecked,
  onFolderToggleSelection,
  implicitlySelectedDocGuids,
  showDeepAnalysis,
  topBanner,
  footerExtraContent,
}) => {
  // Current folder selection state (null = All Files)
  const [currentFolder, setCurrentFolder] = useState<FolderTreeType | null>(null);
  const { isEnabled: deepAnalysisEnabled } = useDeepAnalysisAvailability(matterSlug);
  const shouldShowDeepAnalysis = showDeepAnalysis ?? deepAnalysisEnabled;

  // Get timezone for date formatting
  const timezone = getTimezone();

  // Build folder IDs for server-side filtering
  const folderIds = useMemo(
    () => (currentFolder ? [currentFolder.folder_id] : undefined),
    [currentFolder]
  );

  // Fetch documents with server-side folder filtering
  const {
    documents: filteredDocuments,
    isLoading: isLoadingDocuments,
    mutate: refreshDocuments,
  } = useDocuments(matterSlug, undefined, undefined, folderIds, {}, timezone);

  // Fetch folder tree
  const { folderTree, isLoading: isLoadingFolders, error: folderError, mutate: refreshFolders } = useFolderTree(matterSlug);

  // Track visible rows for select all functionality
  const [visibleRows, setVisibleRows] = useState<Document[]>([]);

  // Folder select handler
  const handleFolderSelect = useCallback((folder: FolderTreeType | null) => {
    setCurrentFolder(folder);
  }, []);

  // Wrap upload complete to refresh documents and folder counts
  const handleUploadComplete = useCallback(
    (fileNames?: string[]) => {
      refreshDocuments(); // Refresh filtered documents
      refreshFolders(); // Refresh folder tree to update counts
      onUploadComplete?.(fileNames); // Call parent handler to refresh all documents
    },
    [refreshDocuments, refreshFolders, onUploadComplete]
  );

  // Total documents count (sum of all folder counts)
  const totalDocuments = useMemo(() => {
    return folderTree.reduce((sum, folder) => sum + (folder.document_count || 0), 0);
  }, [folderTree]);

  // Refs for stable callbacks - prevents column recreation
  // Updated synchronously during render (not in useEffect) so isSelected reads current values
  const documentSelectionsCallbackRef = useRef(documentSelections);
  const documentsCallbackRef = useRef(filteredDocuments);
  const onToggleSelectionCallbackRef = useRef(onToggleSelection);
  const implicitlySelectedDocGuidsRef = useRef(implicitlySelectedDocGuids);

  // Refs for extension callbacks - prevents column recreation
  const getDocumentStatusIconsRef = useRef(getDocumentStatusIcons);
  const getDocumentIconOverlayRef = useRef(getDocumentIconOverlay);
  const isSelectionDisabledRef = useRef(isSelectionDisabled);

  // Update refs synchronously during render so cell renderers see current values
  documentSelectionsCallbackRef.current = documentSelections;
  documentsCallbackRef.current = filteredDocuments;
  onToggleSelectionCallbackRef.current = onToggleSelection;
  implicitlySelectedDocGuidsRef.current = implicitlySelectedDocGuids;
  getDocumentStatusIconsRef.current = getDocumentStatusIcons;
  getDocumentIconOverlayRef.current = getDocumentIconOverlay;
  isSelectionDisabledRef.current = isSelectionDisabled;

  // A doc is checked if it's explicitly selected OR implicitly covered.
  const isSelected = useCallback(
    (docGuid: string) =>
      documentSelectionsCallbackRef.current.has(docGuid)
      || (implicitlySelectedDocGuidsRef.current?.has(docGuid) ?? false),
    [] // No dependencies - reads from refs
  );

  // Lookup ref of all docs that have been selected, keyed by guid.
  // Populated via stableOnToggleSelection so all code paths (checkbox, row click,
  // select-all) are captured. Allows handleRemoveDocument to find docs across folders.
  const selectedDocsRef = useRef<Map<string, Document>>(new Map());

  // Sync selectedDocsRef with pre-existing selections when filtered docs load.
  // Covers the case partner path where selections persist across dialog open/close.
  useEffect(() => {
    for (const doc of filteredDocuments) {
      if (documentSelections.has(doc.guid) && !selectedDocsRef.current.has(doc.guid)) {
        selectedDocsRef.current.set(doc.guid, doc);
      }
    }
  }, [filteredDocuments, documentSelections]);

  // Stable wrapper for onToggleSelection — also tracks docs in selectedDocsRef
  const stableOnToggleSelection = useCallback(
    (doc: Document) => {
      if (documentSelectionsCallbackRef.current.has(doc.guid)) {
        selectedDocsRef.current.delete(doc.guid);
      } else {
        selectedDocsRef.current.set(doc.guid, doc);
      }
      onToggleSelectionCallbackRef.current(doc);
    },
    [] // No dependencies - reads from refs
  );

  // Stable wrappers for extension callbacks
  const stableIsSelectionDisabled = useCallback(
    (doc: Document) => isSelectionDisabledRef.current?.(doc) ?? { disabled: false },
    []
  );
  const stableGetDocumentStatusIcons = useCallback(
    (doc: Document) => getDocumentStatusIconsRef.current?.(doc) ?? [],
    []
  );
  const stableGetDocumentIconOverlay = useCallback(
    (doc: Document) => getDocumentIconOverlayRef.current?.(doc) ?? null,
    []
  );

  // Filter toggle state
  const [filterToggleState, setFilterToggleState] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    filterToggles?.forEach(t => { initial[t.id] = t.defaultValue; });
    return initial;
  });

  // Apply consumer filter predicates client-side
  const displayDocuments = useMemo(() => {
    let docs = filteredDocuments;
    if (filterToggles?.length) {
      docs = docs.filter((doc) =>
        filterToggles.every((toggle) => {
          const isOn = filterToggleState[toggle.id] ?? toggle.defaultValue;
          return !isOn || toggle.predicate(doc);
        }),
      );
    }
    if (documentPredicate) {
      docs = docs.filter(documentPredicate);
    }
    return docs;
  }, [filteredDocuments, filterToggles, filterToggleState, documentPredicate]);

  const handleClearSelection = useCallback(() => {
    selectedDocsRef.current.clear();
    onClearSelection();
  }, [onClearSelection]);

  // Handle removing a document from selection.
  // Checks selectedDocsRef first (works across folders), then falls back to
  // the current folder's filtered documents for docs not yet in the ref.
  const handleRemoveDocument = useCallback(
    (docGuid: string) => {
      const doc = selectedDocsRef.current.get(docGuid)
        ?? documentsCallbackRef.current.find(d => d.guid === docGuid);
      if (doc) {
        selectedDocsRef.current.delete(docGuid);
        onToggleSelection(doc);
      }
    },
    [onToggleSelection]
  );

  // Handle row click to toggle selection
  const handleRowClick = useCallback(
    (row: Document) => {
      const disabledResult = stableIsSelectionDisabled(row);
      if (disabledResult.disabled) return;
      stableOnToggleSelection(row);
    },
    [stableOnToggleSelection, stableIsSelectionDisabled]
  );

  const getRowClassName = useCallback(
    (row: Document) => {
      const result = isSelectionDisabledRef.current?.(row);
      return result?.disabled ? 'opacity-50 bg-gray-50' : undefined;
    },
    []
  );

  // Select all page logic - use document GUIDs for selection tracking
  // Deduplicate: multiple rows can share a guid (e.g. same document in different folders),
  // and toggling the same guid twice would select then immediately deselect it.
  const visibleRowIds = useMemo(
    () => [...new Set(visibleRows.map(row => row.guid))],
    [visibleRows]
  );

  // Ref for visibleRowIds used by handleSelectAllPage
  const visibleRowIdsRef = useRef(visibleRowIds);
  visibleRowIdsRef.current = visibleRowIds;

  // Stable handler that reads from refs - prevents column recreation
  const handleSelectAllPage = useCallback(
    (select: boolean) => {
      visibleRowIdsRef.current.forEach(guid => {
        const currentlySelected = documentSelectionsCallbackRef.current.has(guid);
        const doc = documentsCallbackRef.current.find(d => d.guid === guid);
        if (!doc) return;
        if (select && !currentlySelected) {
          // Skip disabled documents
          const disabledResult = isSelectionDisabledRef.current?.(doc);
          if (disabledResult?.disabled) return;
          stableOnToggleSelection(doc);
        } else if (!select && currentlySelected) {
          stableOnToggleSelection(doc);
        }
      });
    },
    [] // No dependencies - reads from refs
  );

  // Check if we're viewing the Correspondence folder
  const isCorrespondenceFolder = currentFolder ? checkIsCorrespondenceFolder(currentFolder) : false;

  // Generate columns for selection mode - use correspondence columns for Correspondence folder
  // All callbacks use refs/getters to prevent column recreation, which would close filter popovers
  // Select-all checkbox uses SelectionStateContext to get selection state and trigger re-renders
  const columns = useMemo(
    () => {
      const columnParams = {
        isSelected,
        onToggleSelection: stableOnToggleSelection,
        onSelectAllPage: handleSelectAllPage,
        matterSlug,
        isSelectionDisabled: isSelectionDisabled ? stableIsSelectionDisabled : undefined,
        getDocumentStatusIcons: getDocumentStatusIcons ? stableGetDocumentStatusIcons : undefined,
        getDocumentIconOverlay: getDocumentIconOverlay ? stableGetDocumentIconOverlay : undefined,
      };

      return isCorrespondenceFolder
        ? ColumnsCorrespondenceSelectionMode({ ...columnParams, documents: filteredDocuments })
        : ColumnsCaseFilesSelectionMode(columnParams);
    },
    [isSelected, stableOnToggleSelection, handleSelectAllPage, matterSlug, isCorrespondenceFolder, filteredDocuments, stableIsSelectionDisabled, stableGetDocumentStatusIcons, stableGetDocumentIconOverlay, isSelectionDisabled, getDocumentStatusIcons, getDocumentIconOverlay]
  );

  // Triggers re-renders when selection (explicit or implicit) changes.
  const selectionContextValue = useMemo(
    () => ({ documentSelections, implicitlySelectedDocGuids }),
    [documentSelections, implicitlySelectedDocGuids]
  );

  // Build folder path for breadcrumb navigation
  const folderPath = useBreadcrumbPath(currentFolder, folderTree);

  return (
    <SelectionStateContext.Provider value={selectionContextValue}>
    <div className={cn("flex flex-col h-full", className)}>
      {topBanner ? <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">{topBanner}</div> : null}
      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {/* Folder tree sidebar */}
        <div className={"w-72 border-r border-gray-200 bg-gray-50 overflow-y-auto shrink-0"}>
          {isLoadingFolders ? (
            <div className="flex items-center justify-center py-8">
              <Loading />
            </div>
          ) : folderError ? (
            <div className="flex items-center justify-center py-8 px-4">
              <p className="text-sm text-red-600">Failed to load folders</p>
            </div>
          ) : (
            <div className="p-3">
              <FolderTree
                folders={folderTree}
                totalDocuments={totalDocuments}
                matterSlug={matterSlug}
                onRefresh={refreshFolders}
                navigationMode="callback"
                onFolderSelect={handleFolderSelect}
                selectedFolderGuid={currentFolder?.guid || null}
                showFolderActions={false}
                shouldHideFolder={shouldHideFolder}
                selectedFolderGuidsForChecked={selectedFolderGuidsForChecked}
                onFolderToggleSelection={onFolderToggleSelection}
                showDeepAnalysis={shouldShowDeepAnalysis}
              />
            </div>
          )}
        </div>

        {/* File table */}
        <div className="flex-1 overflow-hidden flex flex-col min-w-0">
          {/* Breadcrumb navigation with upload button */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 shrink-0 flex items-center gap-4">
            <BreadcrumbNavigation
              folderPath={folderPath}
              currentFolder={currentFolder}
              onFolderSelect={handleFolderSelect}
            />
            {filterToggles && filterToggles.length > 0 && (
              <div className="flex shrink-0 items-center gap-3">
                {filterToggles.map(toggle => (
                  <label key={toggle.id} className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none whitespace-nowrap">
                    <Checkbox
                      checked={filterToggleState[toggle.id] ?? toggle.defaultValue}
                      onCheckedChange={() => setFilterToggleState(prev => ({
                        ...prev,
                        [toggle.id]: !(prev[toggle.id] ?? toggle.defaultValue),
                      }))}
                      className="h-3.5 w-3.5"
                    />
                    {toggle.label}
                  </label>
                ))}
              </div>
            )}
            {showUploadButton && (
              <div className="shrink-0">
                <FileUpload
                  matterGuid={matterSlug}
                  onUploadComplete={handleUploadComplete}
                  showAsDialog={true}
                  acceptTypes={SUPPORTED_FILE_TYPES_STRING}
                  variant="outline"
                  size="sm"
                  documentType={currentFolder?.folder_id?.toString() || null}
                />
              </div>
            )}
          </div>

          {/* Data table */}
          <div className="flex-1 overflow-auto">
            <DataTable
              columns={columns}
              data={displayDocuments}
              hasPopupMenu={false}
              uploadButton={false}
              exportButton={false}
              hideSearch={true}
              matterGuid={matterSlug}
              isLoading={isLoadingDocuments}
              selectionMode="external"
              onRowClick={handleRowClick}
              onVisibleRowsChange={setVisibleRows}
              resetFiltersKey={currentFolder?.guid ?? 'all-files'}
              getRowClassName={isSelectionDisabled ? getRowClassName : undefined}
            />
          </div>
        </div>
      </div>

      {/* Footer with selected documents and Done button */}
      <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 shrink-0 space-y-2">
        {footerExtraContent}
        <div className="flex items-end gap-4">
          <div className="flex-1 min-w-0">
            {documentSelections.size > 0 || (selectedFolderGuidsForChecked?.size ?? 0) > 0 ? (
              <SelectedDocumentsPanel
                documentSelections={documentSelections}
                onRemoveDocument={handleRemoveDocument}
                onClearAll={handleClearSelection}
                matterSlug={matterSlug}
                selectedFolderCount={selectedFolderGuidsForChecked?.size ?? 0}
              />
            ) : (
              <span className="text-sm text-gray-500">No documents selected</span>
            )}
          </div>
          {onDone && (
            <div className="shrink-0 pb-1">
              <Button onClick={onDone} disabled={doneDisabled}>
                {doneLabel}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
    </SelectionStateContext.Provider>
  );
};

export default DocumentSelector;
